import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getIdTokenResult, onAuthStateChanged, signOut } from "firebase/auth";
import { getDbInterfaceAuth } from "../../../firebase";
import {
  API_FAILURE_KINDS,
  AUTHENTICATION_SCOPES,
  clearStepUpToken,
  subscribeToApiFailures,
} from "../../utils/commHelper";
import { SESSION_ENDED_REASONS, computeSessionEndsAt } from "./dbInterfaceSessionTiming";

const EXPIRY_CHECK_INTERVAL_MILLISECONDS = 1000;

// Owns the DB Interface sign-in for this tab: the live Firebase user, when the one-hour
// session ends, the automatic sign-out, and the reaction to the server's 401s.
//
// Nothing here changes once a second. An earlier version kept a ticking `now` in state and
// returned the remaining time from it, which re-rendered every consumer of the session
// context — including the whole data table — once a second. The countdown is rendered by
// DbInterfaceSessionBar, which ticks locally; this hook only exposes the fixed end time.
export function useDbInterfaceSession() {
  // undefined = Firebase is still restoring the tab's session, null = signed out.
  const [user, setUser] = useState(undefined);
  const [sessionEndsAt, setSessionEndsAt] = useState(null);
  const [sessionEndedReason, setSessionEndedReason] = useState(null);
  const [stepUpRequest, setStepUpRequest] = useState(null);
  const endingSessionRef = useRef(false);

  useEffect(() => {
    const auth = getDbInterfaceAuth();
    if (!auth) {
      setUser(null);
      return undefined;
    }
    return onAuthStateChanged(auth, (nextUser) => {
      endingSessionRef.current = false;
      setUser(nextUser);
      if (!nextUser) setSessionEndsAt(null);
    });
  }, []);

  const endSession = useCallback(async (reason) => {
    if (endingSessionRef.current) return;
    endingSessionRef.current = true;
    setSessionEndedReason(reason);
    setSessionEndsAt(null);
    clearStepUpToken();
    try {
      await signOut(getDbInterfaceAuth());
    } catch (e) {
      console.error("[dbInterface] sign-out failed:", e);
      endingSessionRef.current = false;
    }
  }, []);

  // Session end = Firebase auth_time (sign-in moment, unchanged by token refresh) + 1 hour.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getIdTokenResult(user)
      .then((tokenResult) => {
        if (!cancelled) setSessionEndsAt(computeSessionEndsAt(tokenResult.authTime));
      })
      .catch((e) => console.error("[dbInterface] could not read auth time:", e));
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Polls the clock instead of arming one long timer, which would not survive the laptop
  // sleeping. It reads Date.now() directly and sets no state, so a tick costs nothing and
  // renders nothing until the session actually expires.
  useEffect(() => {
    if (!sessionEndsAt) return undefined;
    const checkExpiry = () => {
      if (Date.now() >= sessionEndsAt) {
        endSession(SESSION_ENDED_REASONS.timeLimitReached);
      }
    };
    checkExpiry();
    const intervalId = setInterval(checkExpiry, EXPIRY_CHECK_INTERVAL_MILLISECONDS);
    return () => clearInterval(intervalId);
  }, [sessionEndsAt, endSession]);

  // The server is authoritative: its 401 ends the session even if the local clock disagrees.
  useEffect(() => {
    return subscribeToApiFailures((failure) => {
      if (failure.apiTarget?.authenticationScope !== AUTHENTICATION_SCOPES.dbInterface) return;
      if (failure.kind === API_FAILURE_KINDS.sessionExpired) {
        endSession(SESSION_ENDED_REASONS.serverRejectedSession);
      } else if (failure.kind === API_FAILURE_KINDS.stepUpRequired) {
        setStepUpRequest({ requestedAt: Date.now() });
      }
    });
  }, [endSession]);

  const clearSessionEndedReason = useCallback(() => setSessionEndedReason(null), []);
  const clearStepUpRequest = useCallback(() => setStepUpRequest(null), []);

  // Memoised so the session context keeps a stable identity between renders: consumers
  // re-render when the session really changes, not on every render of the gate.
  return useMemo(
    () => ({
      user,
      sessionEndsAt,
      endSession,
      sessionEndedReason,
      clearSessionEndedReason,
      stepUpRequest,
      clearStepUpRequest,
    }),
    [user, sessionEndsAt, endSession, sessionEndedReason, clearSessionEndedReason, stepUpRequest, clearStepUpRequest]
  );
}
